/**
 * sf-bundle-builder.js
 * Powers the "Bundle builder" section (buy X get Y free style offers).
 *
 * IMPORTANT: This script only calculates and displays the discount on the
 * front end so shoppers can see the offer working in real time. It does NOT
 * change what they're charged. To make the discount real at checkout, create
 * a matching Shopify automatic discount (Discounts > Create discount >
 * Buy X get Y, set to the lowest priced item) in your Shopify Admin, using
 * the same "buy_qty" / "free_qty" numbers you set in the section settings.
 */

(function () {
  'use strict';

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    var locale = document.documentElement.lang || 'en-US';
    try {
      return (cents / 100).toLocaleString(locale, { style: 'currency', currency: currency });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function initBundleBuilder(root) {
    if (root.dataset.sfInitialized === 'true') return;
    root.dataset.sfInitialized = 'true';

    var buyQty = parseInt(root.dataset.buyQty, 10) || 3;
    var freeQty = parseInt(root.dataset.freeQty, 10) || 1;
    var showShipping = root.dataset.showShipping === 'true';
    var shippingCost = parseInt(root.dataset.shippingCost, 10) || 0;
    var freeShippingWhenUnlocked = root.dataset.freeShippingWhenUnlocked === 'true';

    var progressFill = root.querySelector('[data-sf-progress-fill]');
    var progressLabel = root.querySelector('[data-sf-progress-label]');
    var summaryList = root.querySelector('[data-sf-summary-list]');
    var summaryEmpty = root.querySelector('[data-sf-summary-empty]');
    var summaryTotals = root.querySelector('[data-sf-summary-totals]');
    var subtotalEl = root.querySelector('[data-sf-summary-subtotal]');
    var discountEl = root.querySelector('[data-sf-summary-discount]');
    var discountRow = root.querySelector('[data-sf-discount-row]');
    var shippingEl = root.querySelector('[data-sf-summary-shipping]');
    var shippingRow = root.querySelector('[data-sf-shipping-row]');
    var totalEl = root.querySelector('[data-sf-summary-total]');
    var ctaButton = root.querySelector('[data-sf-add-bundle-to-cart]');
    var clearButton = root.querySelector('[data-sf-clear-bundle]');

    // Read the raw "Add {qty} more to unlock {free} free" template from a
    // data attribute rather than the live text node, so it can't get
    // corrupted by earlier renders or whitespace differences.
    var progressTemplate = progressLabel && progressLabel.dataset.template
      ? progressLabel.dataset.template
      : 'Add {qty} more to unlock {free} free';

    var items = []; // { uid, variantId, title, price, image }
    var uidCounter = 0;

    function addItem(productEl) {
      uidCounter += 1;
      items.push({
        uid: uidCounter,
        variantId: productEl.dataset.variantId,
        title: productEl.dataset.title,
        price: parseInt(productEl.dataset.price, 10) || 0,
        image: productEl.dataset.image
      });
      productEl.classList.add('is-selected');
      render();
    }

    function removeItem(uid) {
      items = items.filter(function (item) {
        return item.uid !== uid;
      });
      syncSelectedStates();
      render();
    }

    function clearAll() {
      items = [];
      syncSelectedStates();
      render();
    }

    function syncSelectedStates() {
      var selectedVariantIds = items.map(function (i) { return i.variantId; });
      root.querySelectorAll('[data-sf-bundle-product]').forEach(function (el) {
        el.classList.toggle('is-selected', selectedVariantIds.indexOf(el.dataset.variantId) !== -1);
      });
    }

    // For every full group of `buyQty` items, the cheapest `freeQty`
    // item(s) in that group become free. Remainder items (a partial group)
    // are charged at full price until the group is completed.
    function calculatePricing() {
      var sorted = items.slice().sort(function (a, b) { return a.price - b.price; });
      var freeUids = {};
      var fullGroups = Math.floor(sorted.length / buyQty);
      var freeSlotsAvailable = fullGroups * freeQty;

      for (var i = 0; i < sorted.length && freeSlotsAvailable > 0; i++) {
        freeUids[sorted[i].uid] = true;
        freeSlotsAvailable--;
      }

      var subtotal = 0;
      var discount = 0;
      items.forEach(function (item) {
        subtotal += item.price;
        if (freeUids[item.uid]) discount += item.price;
      });

      return {
        freeUids: freeUids,
        subtotal: subtotal,
        discount: discount,
        total: subtotal - discount
      };
    }

    function render() {
      var pricing = calculatePricing();

      // Progress bar
      var remainderInCycle = items.length % buyQty;
      var itemsNeeded = remainderInCycle === 0 ? (items.length === 0 ? buyQty : 0) : (buyQty - remainderInCycle);
      var progressPercent = items.length === 0 ? 0 : (remainderInCycle === 0 ? 100 : (remainderInCycle / buyQty) * 100);

      if (progressFill) progressFill.style.width = progressPercent + '%';
      if (progressLabel) {
        if (itemsNeeded === 0 && items.length > 0) {
          progressLabel.textContent = freeQty === 1
            ? 'Your free item is unlocked!'
            : 'Your ' + freeQty + ' free items are unlocked!';
        } else {
          progressLabel.textContent = progressTemplate
            .replace('{qty}', itemsNeeded)
            .replace('{free}', freeQty);
        }
      }

      // Summary list
      if (summaryList) {
        summaryList.innerHTML = '';
        items.forEach(function (item) {
          var isFree = !!pricing.freeUids[item.uid];
          var li = document.createElement('li');
          li.className = 'sf-bundle-summary__item';
          li.innerHTML =
            '<img src="' + (item.image || '') + '" alt="" loading="lazy" width="40" height="40">' +
            '<span class="sf-bundle-summary__item-title">' + item.title + '</span>' +
            '<span class="sf-bundle-summary__item-price' + (isFree ? ' sf-bundle-summary__item-price--free' : '') + '">' +
              (isFree ? '<s>' + formatMoney(item.price) + '</s>Free' : formatMoney(item.price)) +
            '</span>' +
            '<button type="button" class="sf-bundle-summary__item-remove" data-sf-remove-item="' + item.uid + '" aria-label="Remove">&times;</button>';
          summaryList.appendChild(li);
        });
      }

      var hasItems = items.length > 0;
      if (summaryEmpty) summaryEmpty.hidden = hasItems;
      if (summaryTotals) summaryTotals.hidden = !hasItems;
      if (clearButton) clearButton.hidden = !hasItems;
      if (ctaButton) ctaButton.disabled = !hasItems;

      if (subtotalEl) subtotalEl.textContent = formatMoney(pricing.subtotal);
      if (pricing.discount > 0) {
        if (discountRow) discountRow.hidden = false;
        if (discountEl) discountEl.textContent = '-' + formatMoney(pricing.discount);
      } else if (discountRow) {
        discountRow.hidden = true;
      }

      // Shipping (display only — real shipping is still calculated at checkout)
      var isUnlocked = itemsNeeded === 0 && hasItems;
      var shippingIsFree = freeShippingWhenUnlocked && isUnlocked;
      var shippingAmount = shippingIsFree ? 0 : shippingCost;

      if (showShipping && hasItems) {
        if (shippingRow) shippingRow.hidden = false;
        if (shippingEl) {
          shippingEl.textContent = shippingIsFree ? 'Free' : formatMoney(shippingAmount);
          shippingEl.classList.toggle('sf-bundle-summary__item-price--free', shippingIsFree);
        }
      } else if (shippingRow) {
        shippingRow.hidden = true;
      }

      var grandTotal = pricing.total + (showShipping ? shippingAmount : 0);
      if (totalEl) totalEl.textContent = formatMoney(grandTotal);
    }

    // Single delegated listener for the whole section — more reliable than
    // binding to each button individually, and keeps working even if cards
    // are re-rendered by the theme editor.
    root.addEventListener('click', function (event) {
      var addBtn = event.target.closest('[data-sf-add-to-bundle]');
      if (addBtn) {
        if (addBtn.disabled) return;
        var productEl = addBtn.closest('[data-sf-bundle-product]');
        if (productEl) addItem(productEl);
        return;
      }

      var removeBtn = event.target.closest('[data-sf-remove-item]');
      if (removeBtn) {
        removeItem(parseInt(removeBtn.dataset.sfRemoveItem, 10));
        return;
      }

      if (event.target.closest('[data-sf-clear-bundle]')) {
        clearAll();
        return;
      }

      if (event.target.closest('[data-sf-add-bundle-to-cart]')) {
        addBundleToCart();
      }
    });

    function addBundleToCart() {
      if (!ctaButton || items.length === 0) return;

      ctaButton.disabled = true;
      var originalLabel = ctaButton.textContent;
      ctaButton.textContent = 'Adding...';

      var payload = {
        items: items.map(function (item) {
          return { id: item.variantId, quantity: 1 };
        })
      };

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.documentElement.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          window.location.href = window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_url
            ? window.Shopify.routes.cart_url
            : '/cart';
        })
        .catch(function (err) {
          console.error('Bundle add to cart failed', err);
          alert(err && err.description ? err.description : 'Something went wrong adding this bundle to your cart.');
          ctaButton.disabled = false;
          ctaButton.textContent = originalLabel;
        });
    }

    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.sf-bundle-builder').forEach(initBundleBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();